/**
 * /equipment/pressure-control/tests/new — record a BOP pressure test (api#1281,
 * BOP-2). One test lists every component it pressured, each with its own low /
 * high test pressure, hold times and pass/fail; the gauge that read it; an
 * optional stack; and an optional chart photo.
 *
 * WHAT THE API DECIDES, NOT THIS PAGE. POST /tests refuses (409) a gauge that
 * was not calibrated for the test day or whose calibrated range is below the
 * highest pressure, and any component that could not be on a well that day.
 * Those sentences are shown as the API wrote them, one line per refused
 * component. The overall result is the API's too (fail if any item failed).
 *
 * PRE-FILL. Choosing a component asks GET /components/:id/test-defaults (its
 * own defaults, else its class's) and fills only the blanks, so a figure the
 * tester already typed is never overwritten. Pressures are typed in the
 * tenant's unit (`meta.pressureUnit`), which is what the API assumes.
 *
 * THE CHART. The API files a chart against the TEST's id, so the id is minted
 * here once per visit and kept across retries: a refused test can be fixed and
 * sent again with the same id and the chart already uploaded. A
 * `pressure_control_chart` upload cannot be deleted (the API refuses), so
 * nothing here offers to remove it.
 *
 * TIME. "Tested at" is the tenant's wall clock (datetime-local read in the
 * tenant's zone), sent as an instant; the API resolves the day.
 *
 * ANOTHER DOOR, THE SAME FORM. With a `target`, the identical form posts
 * somewhere else — today a Well Servicing tour sheet's field door
 * (fieldiq-api#1324: POST /well-servicing/field/tours/:id/pressure-tests, the
 * same body, recorded PENDING). The target says who may see the form (the
 * caller has decided), where the record goes, the defaults, how a refusal
 * reads and where to land after. This screen stays vertical-free: the target
 * is built by the vertical's own wrapper.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { putToPresignedUrl } from '../../api/upload';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { PcErrorLine, usePcEquipmentOptions } from '../../components/pressure-control/PcSectionNav';
import { useAuth } from '../../contexts/AuthContext';
import { useCanWriteCompanySettings } from '../../hooks/useCanWriteCompanySettings';
import {
  fetchPcComponentTestDefaults,
  usePcCalibrations,
  usePcComponents,
  useRecordPcTest,
} from '../../hooks/usePressureControl';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { fromDatetimeLocal, toDatetimeLocal } from '../../lib/datetime-local';
import {
  canRecordPcTest,
  PC_CAL_LABEL,
  PC_TEST_KIND_LABEL,
  PC_TESTS,
  pcClassLabel,
  pcDay,
  pcTestRefusal,
} from '../../lib/pressure-control';
import { PC_TEST_KINDS, type PcTestDefault, type PcTestItemWrite, type PcTestWrite } from '../../types/pressure-control';
import { DH_DATE_INPUT, DH_INPUT, DH_SECONDARY, DhField, DhPanel } from '../downhole/DownholeBits';

export interface ItemDraft {
  key: string;
  equipmentId: string;
  low: string;
  lowHold: string;
  high: string;
  highHold: string;
  result: '' | 'pass' | 'fail';
  notes: string;
  /** Where the pre-filled figures came from, for the hint under the row. */
  prefilledFrom: 'component' | 'class' | null;
}

let seq = 0;
export const blankItem = (equipmentId = ''): ItemDraft => ({
  key: `item-${(seq += 1)}`, equipmentId, low: '', lowHold: '', high: '', highHold: '', result: '', notes: '', prefilledFrom: null,
});

const mintId = (): string => {
  const c: any = (globalThis as any).crypto;
  if (c?.randomUUID) return c.randomUUID();
  // RFC 4122 v4 from Math.random — only where randomUUID is missing (an old WebView).
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    return (ch === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};

const num = (s: string): number | null => {
  const t = s.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : Number.NaN;
};

/**
 * The POST items, or a sentence naming the first problem. Only the shape is
 * checked here (required fields, numbers, low with its hold, low below high);
 * every rule about the components and the gauge is the API's.
 */
export function pcTestItemsBody(items: ItemDraft[], unit: string): PcTestItemWrite[] | string {
  if (items.length === 0) return 'Add at least one tested component.';
  const out: PcTestItemWrite[] = [];
  const seen = new Set<string>();
  for (let i = 0; i < items.length; i += 1) {
    const it = items[i];
    const n = `Component ${i + 1}`;
    if (!it.equipmentId) return `${n}: choose the component.`;
    if (seen.has(it.equipmentId)) return `${n}: that component is already listed.`;
    seen.add(it.equipmentId);
    const high = num(it.high);
    const highHold = num(it.highHold);
    const low = num(it.low);
    const lowHold = num(it.lowHold);
    if (high === null || Number.isNaN(high) || high <= 0) return `${n}: enter the high test pressure (${unit}).`;
    if (highHold === null || !Number.isInteger(highHold) || highHold < 1 || highHold > 240) return `${n}: enter the high hold in whole minutes (1 to 240).`;
    if (low !== null && (Number.isNaN(low) || low <= 0)) return `${n}: the low test pressure must be a number above 0 (${unit}).`;
    if (lowHold !== null && (!Number.isInteger(lowHold) || lowHold < 1 || lowHold > 240)) return `${n}: the low hold must be whole minutes (1 to 240).`;
    if ((low === null) !== (lowHold === null)) return `${n}: a low test needs both its pressure and its hold.`;
    if (low !== null && low >= high) return `${n}: the low test pressure must be below the high.`;
    if (it.result !== 'pass' && it.result !== 'fail') return `${n}: choose pass or fail.`;
    out.push({
      equipment_id: it.equipmentId,
      low_test_pressure: low,
      low_hold_minutes: lowHold,
      high_test_pressure: high,
      high_hold_minutes: highHold,
      result: it.result,
      notes: it.notes.trim() || null,
    });
  }
  return out;
}

/** Record the test through another door (see the header). */
export interface PcTestRecordTarget {
  eyebrow: string;
  title: string;
  /** ISO instant to start "Tested at" from; now when absent. */
  defaultAt?: string;
  defaultRef?: string;
  send: (body: PcTestWrite) => Promise<{ warnings: Array<{ message?: string | null }> }>;
  /** A refused send, as the sentence to show. */
  refusal: (e: unknown, fallback: string) => string;
  successMessage: string;
  done: () => void;
}

export default function PressureControlTestNewScreen({ target }: { target?: PcTestRecordTarget } = {}) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const elevated = useCanWriteCompanySettings();
  // With a target the caller decides who sees the form (and must, before drawing it); its API decides the rest.
  const canRecord = target ? true : canRecordPcTest(user?.role, elevated);
  const tz = useTenantTimeZone();

  const components = usePcComponents(canRecord);
  const calibrations = usePcCalibrations(canRecord);
  const equipment = usePcEquipmentOptions(canRecord);
  const record = useRecordPcTest();
  const unit = components.data?.meta?.pressureUnit ?? '';

  const [testId] = useState(mintId);
  const [testedAt, setTestedAt] = useState(() => toDatetimeLocal(target?.defaultAt ?? new Date().toISOString(), tz));
  const [kind, setKind] = useState('');
  const [gauge, setGauge] = useState('');
  const [stack, setStack] = useState('');
  const [items, setItems] = useState<ItemDraft[]>(() => [blankItem(params.get('component') ?? '')]);
  const [testedByName, setTestedByName] = useState('');
  const [witnessedBy, setWitnessedBy] = useState('');
  const [contextRef, setContextRef] = useState(target?.defaultRef ?? '');
  const [notes, setNotes] = useState('');
  const [chart, setChart] = useState<File | null>(null);
  const [chartUploaded, setChartUploaded] = useState<{ file: File; uploadId: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [failures, setFailures] = useState<Array<{ equipmentId: string; error: string }>>([]);

  const all = useMemo(() => components.data?.rows ?? [], [components.data]);
  const byId = useMemo(() => new Map(all.map((c) => [c.equipmentId, c])), [all]);
  const stacks = all.filter((c) => c.spec.componentClass === 'stack_assembly');
  const names = useMemo(() => new Map((equipment.data ?? []).map((e) => [e.id, e.name])), [equipment.data]);

  // The gauges: equipment with a calibration on file, each with its LATEST
  // calibration's state (the API orders by equipment, newest first). A gauge
  // with none would be refused, so it is not offered.
  const gauges = useMemo(() => {
    const seen = new Map<string, { id: string; status: string; dueOn: string | null }>();
    for (const c of calibrations.data ?? []) {
      if (!seen.has(c.equipmentId)) seen.set(c.equipmentId, { id: c.equipmentId, status: String(c.state.status ?? ''), dueOn: c.dueOn });
    }
    return Array.from(seen.values()).map((g) => ({ ...g, name: names.get(g.id) ?? '—' })).sort((a, b) => a.name.localeCompare(b.name));
  }, [calibrations.data, names]);

  const prefill = async (key: string, equipmentId: string) => {
    if (!equipmentId) return;
    let d: PcTestDefault | null;
    try {
      d = await fetchPcComponentTestDefaults(equipmentId);
    } catch {
      return; // No defaults is an ordinary answer; a failed read just leaves the row blank.
    }
    if (!d) return;
    const fill = (cur: string, v: number | null) => (cur.trim() === '' && v !== null ? String(v) : cur);
    setItems((prev) => prev.map((it) => (it.key !== key || it.equipmentId !== equipmentId ? it : {
      ...it,
      low: fill(it.low, d!.lowTestPressure),
      lowHold: fill(it.lowHold, d!.lowHoldMinutes),
      high: fill(it.high, d!.highTestPressure),
      highHold: fill(it.highHold, d!.highHoldMinutes),
      prefilledFrom: d!.source ?? null,
    })));
  };

  // The component named in ?component= is pre-filled once, on mount.
  const initial = useRef(items[0]);
  useEffect(() => {
    if (canRecord && initial.current?.equipmentId) void prefill(initial.current.key, initial.current.equipmentId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setItem = (key: string, patch: Partial<ItemDraft>) =>
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...patch } : it)));

  const chooseComponent = (key: string, equipmentId: string) => {
    setItem(key, { equipmentId, prefilledFrom: null });
    void prefill(key, equipmentId);
  };

  const addStackComponents = () => {
    const have = new Set(items.map((i) => i.equipmentId));
    const add = all.filter((c) => c.spec.stackParentId === stack && !have.has(c.equipmentId));
    if (add.length === 0) return;
    const fresh = add.map((c) => blankItem(c.equipmentId));
    setItems((prev) => [...prev.filter((i) => i.equipmentId !== ''), ...fresh]);
    fresh.forEach((f) => void prefill(f.key, f.equipmentId));
  };

  const submit = async () => {
    setError(null);
    setFailures([]);
    const at = fromDatetimeLocal(testedAt, tz);
    if (!at) return setError('Enter when the test was done.');
    if (!kind) return setError('Choose the kind of test.');
    if (!gauge) return setError('Choose the gauge that read the test.');
    if (items.some((i) => i.equipmentId === gauge)) return setError('The gauge cannot also be a tested component.');
    const itemBody = pcTestItemsBody(items, unit || 'tenant unit');
    if (typeof itemBody === 'string') return setError(itemBody);
    setBusy(true);
    let chartUploadId: string | null = null;
    try {
      if (chart) {
        if (chartUploaded && chartUploaded.file === chart) {
          chartUploadId = chartUploaded.uploadId;
        } else {
          const contentType = chart.type || 'image/jpeg';
          const { data: presigned } = await api.uploads.getPresignedUrl({
            fileName: chart.name || 'chart.jpg', contentType, entityType: 'pressure_control_chart', entityId: testId,
          });
          // Throws unless storage answered 2xx. No Content-Type is set by hand on the
          // API calls; the interceptor owns that.
          await putToPresignedUrl(presigned.uploadUrl, chart, contentType);
          await api.uploads.confirm({ key: presigned.key, size: chart.size });
          if (!presigned.uploadId) throw new Error('The chart upload did not return an id.');
          chartUploadId = presigned.uploadId;
          setChartUploaded({ file: chart, uploadId: presigned.uploadId });
        }
      }
    } catch (e: any) {
      setBusy(false);
      return setError(e?.userMessage || pcTestRefusal(e, 'The chart photo could not be uploaded.').message);
    }
    const body: PcTestWrite = {
      id: testId,
      tested_at: at,
      test_kind: kind,
      items: itemBody,
      gauge_equipment_id: gauge,
      stack_equipment_id: stack || null,
      tested_by_name: testedByName.trim() || null,
      witnessed_by: witnessedBy.trim() || null,
      context_ref: contextRef.trim() || null,
      chart_upload_id: chartUploadId,
      notes: notes.trim() || null,
    };
    try {
      if (target) {
        const out = await target.send(body);
        setBusy(false);
        toast.success(target.successMessage);
        for (const w of out.warnings) if (w.message) toast.warning(w.message);
        target.done();
        return;
      }
      const out = await record.mutateAsync(body);
      setBusy(false);
      toast.success('Pressure test recorded');
      for (const w of out.warnings) toast.warning(w.message);
      navigate(`${PC_TESTS}/${out.test?.id ?? testId}`, { replace: true });
    } catch (e) {
      setBusy(false);
      const r = pcTestRefusal(e, 'The test could not be recorded.');
      setError(target ? target.refusal(e, 'The test could not be recorded.') : r.message);
      setFailures(r.failures);
    }
  };

  if (!canRecord) {
    return (
      <div data-testid="pc-test-new-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
        <PageHeaderBand eyebrow="Pressure control" title="Record a pressure test" onBack={() => navigate(-1)} backLabel="Back" />
        <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)]">
          <div className="mx-auto max-w-[1920px]">
            <DhPanel eyebrow="Pressure test" title="Not available">
              <p className="px-4 py-6 text-sm text-fieldiq-text">
                Your role cannot record a pressure test. An admin, office staff or a supervisor can.{' '}
                <Link to={PC_TESTS} className="font-semibold underline underline-offset-2">Back to the tests</Link>
              </p>
            </DhPanel>
          </div>
        </section>
      </div>
    );
  }

  const usedIds = new Set(items.map((i) => i.equipmentId).filter(Boolean));

  return (
    <div data-testid="pc-test-new-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={target?.eyebrow ?? 'Pressure control'}
        title={target?.title ?? 'Record a pressure test'}
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          <button type="button" onClick={submit} disabled={busy} className="fi-action fi-action--standard fi-action--primary">
            {busy && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            Record test
          </button>
        }
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <DhPanel eyebrow="Pressure test" title="The test" testId="pc-test-form">
            <div className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
              <DhField label="Tested at" hint={tz ? `Your company's clock (${tz}).` : undefined}>
                <input type="datetime-local" className={DH_DATE_INPUT} value={testedAt} onChange={(e) => setTestedAt(e.target.value)} aria-label="Tested at" />
              </DhField>
              <DhField label="Kind">
                <select className={DH_INPUT} value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Kind">
                  <option value="">Choose…</option>
                  {PC_TEST_KINDS.map((k) => <option key={k} value={k}>{PC_TEST_KIND_LABEL[k]}</option>)}
                </select>
              </DhField>
              <DhField
                label="Gauge"
                hint={
                  calibrations.isError
                    ? 'The gauges could not be loaded.'
                    : gauges.length === 0 && calibrations.data
                      ? 'No gauge has a calibration on file. Record one under Gauges first.'
                      : 'Gauges with a calibration on file. The API checks it was valid on the test day.'
                }
              >
                <select className={DH_INPUT} value={gauge} onChange={(e) => setGauge(e.target.value)} aria-label="Gauge">
                  <option value="">Choose…</option>
                  {gauges.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name} · {PC_CAL_LABEL[g.status] ?? '—'} · valid through {pcDay(g.dueOn)}
                    </option>
                  ))}
                </select>
              </DhField>
              <DhField label="Stack" hint="Optional: the stack assembly tested.">
                <select className={DH_INPUT} value={stack} onChange={(e) => setStack(e.target.value)} aria-label="Stack">
                  <option value="">None</option>
                  {stacks.map((s) => <option key={s.equipmentId} value={s.equipmentId}>{s.name ?? '—'}</option>)}
                </select>
              </DhField>
              <DhField label="Tested by" hint="Blank: you.">
                <input className={DH_INPUT} value={testedByName} maxLength={200} onChange={(e) => setTestedByName(e.target.value)} aria-label="Tested by" />
              </DhField>
              <DhField label="Witnessed by">
                <input className={DH_INPUT} value={witnessedBy} maxLength={200} onChange={(e) => setWitnessedBy(e.target.value)} aria-label="Witnessed by" />
              </DhField>
              <DhField label="Reference" hint="A job, well or tour number. Optional.">
                <input className={DH_INPUT} value={contextRef} maxLength={100} onChange={(e) => setContextRef(e.target.value)} aria-label="Reference" />
              </DhField>
              <DhField label="Chart photo" hint="Optional. Once attached to a test it cannot be removed.">
                <input type="file" accept="image/*" className="block w-full text-sm text-fieldiq-text" onChange={(e) => setChart(e.target.files?.[0] ?? null)} aria-label="Chart photo" />
              </DhField>
              <div className="sm:col-span-2 lg:col-span-4">
                <DhField label="Notes">
                  <textarea className={`${DH_INPUT} h-20 py-2`} value={notes} maxLength={4000} onChange={(e) => setNotes(e.target.value)} aria-label="Notes" />
                </DhField>
              </div>
            </div>
          </DhPanel>

          <DhPanel
            eyebrow={unit ? `Pressures in ${unit}` : 'Pressures'}
            title="Tested components"
            testId="pc-test-items"
            actions={
              <div className="flex flex-wrap gap-2">
                {stack && (
                  <button type="button" onClick={addStackComponents} className={DH_SECONDARY}>
                    Add the stack&rsquo;s components
                  </button>
                )}
                <button type="button" onClick={() => setItems((p) => [...p, blankItem()])} className={DH_SECONDARY}>
                  <Plus className="size-4" aria-hidden="true" />
                  Add component
                </button>
              </div>
            }
            footer="A test cannot be edited once recorded. To correct one, record a new test."
          >
            <div className="divide-y divide-fieldiq-border">
              {items.map((it, i) => (
                <div key={it.key} className="grid gap-3 px-4 py-4 sm:grid-cols-2 lg:grid-cols-8" data-testid="pc-test-item">
                  <div className="sm:col-span-2">
                    <DhField label={`Component ${i + 1}`}>
                      <select className={DH_INPUT} value={it.equipmentId} onChange={(e) => chooseComponent(it.key, e.target.value)} aria-label={`Component ${i + 1}`}>
                        <option value="">Choose…</option>
                        {all
                          .filter((c) => c.equipmentId === it.equipmentId || !usedIds.has(c.equipmentId))
                          .map((c) => (
                            <option key={c.equipmentId} value={c.equipmentId}>
                              {c.name ?? '—'} · {pcClassLabel(c.spec.componentClass)}
                            </option>
                          ))}
                      </select>
                    </DhField>
                    {it.prefilledFrom && (
                      <p className="mt-1 text-xs text-fieldiq-muted">
                        Pre-filled from {it.prefilledFrom === 'component' ? "this component's" : "its class's"} default test pressures.
                      </p>
                    )}
                    {it.equipmentId && byId.get(it.equipmentId) === undefined && components.data && (
                      <p className="mt-1 text-xs text-fieldiq-muted">Not a registered component.</p>
                    )}
                  </div>
                  <DhField label={`Low (${unit || '—'})`}>
                    <input className={DH_INPUT} inputMode="decimal" value={it.low} onChange={(e) => setItem(it.key, { low: e.target.value })} aria-label={`Component ${i + 1} low test pressure`} />
                  </DhField>
                  <DhField label="Low hold (min)">
                    <input className={DH_INPUT} inputMode="numeric" value={it.lowHold} onChange={(e) => setItem(it.key, { lowHold: e.target.value })} aria-label={`Component ${i + 1} low hold minutes`} />
                  </DhField>
                  <DhField label={`High (${unit || '—'})`}>
                    <input className={DH_INPUT} inputMode="decimal" value={it.high} onChange={(e) => setItem(it.key, { high: e.target.value })} aria-label={`Component ${i + 1} high test pressure`} />
                  </DhField>
                  <DhField label="High hold (min)">
                    <input className={DH_INPUT} inputMode="numeric" value={it.highHold} onChange={(e) => setItem(it.key, { highHold: e.target.value })} aria-label={`Component ${i + 1} high hold minutes`} />
                  </DhField>
                  <DhField label="Result">
                    <select className={DH_INPUT} value={it.result} onChange={(e) => setItem(it.key, { result: e.target.value as ItemDraft['result'] })} aria-label={`Component ${i + 1} result`}>
                      <option value="">Choose…</option>
                      <option value="pass">Pass</option>
                      <option value="fail">Fail</option>
                    </select>
                  </DhField>
                  <div className="flex items-end">
                    <button
                      type="button"
                      onClick={() => setItems((p) => p.filter((x) => x.key !== it.key))}
                      disabled={items.length === 1}
                      className={DH_SECONDARY}
                      aria-label={`Remove component ${i + 1} from this test`}
                    >
                      <Trash2 className="size-4" aria-hidden="true" />
                    </button>
                  </div>
                  <div className="sm:col-span-2 lg:col-span-8">
                    <DhField label="Item notes">
                      <input className={DH_INPUT} value={it.notes} maxLength={2000} onChange={(e) => setItem(it.key, { notes: e.target.value })} aria-label={`Component ${i + 1} notes`} />
                    </DhField>
                  </div>
                </div>
              ))}
            </div>
          </DhPanel>

          <PcErrorLine message={error}>
            {failures.length > 0 && (
              <ul className="mt-2 list-disc space-y-1 pl-5" data-testid="pc-test-failures">
                {failures.map((f, i) => (
                  <li key={`${f.equipmentId}-${i}`}>{f.error}</li>
                ))}
              </ul>
            )}
          </PcErrorLine>
        </div>
      </section>
    </div>
  );
}
